import url from 'url';
import React, { useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import hasAccess, { reportsAccessId, viewChargerConfigsId, viewFirmwareUpdatePageAccessId } from './HasAccess';
import { selectIsAuthenticated } from '../../screens/Login/selectors';

const SHOW_HOME_KEY = 'home.available';
const SHOW_DASHOARD_KEY = 'dashboard.available';
const SHOW_FIRMWARE_UPDATE_KEY = 'firmwareUpdate.available';
const SHOW_DICTIONARIES_KEY = 'showDictionaries';
const SHOW_REPORTS_KEY = 'showReports';
const SHOW_TECHNICAL_INFO_FIELDS_KEY = 'showTechnicalInfoFields';
const SHOW_BATCH_CONFIGURATION_KEY = 'configurationBatchUpdate.available';

export const getShowHomeFlag = (config) => get(config, SHOW_HOME_KEY, true);
export const getDashBoardFlag = (config) => get(config, SHOW_DASHOARD_KEY, true);
export const getFirmwareUpdateFlag = (config) => get(config, SHOW_FIRMWARE_UPDATE_KEY, true);
export const getShowReportsFlag = (config) => get(config, SHOW_REPORTS_KEY) || false;
export const getShowDictionaryForSAFlag = (config) => get(config, SHOW_DICTIONARIES_KEY) || false;
export const getShowTechnicalInfoFieldsFlag = (config) => get(config, SHOW_TECHNICAL_INFO_FIELDS_KEY) || false;
export const getShowConfigUpdate = (config) => get(config, SHOW_BATCH_CONFIGURATION_KEY, true);

export const REDIRECT_QUERY_PARAM_NAME = 'redirect';

const createRedirectLoc = (allowRedirectBack, location, redirectPath) => {
  const { query, pathname, hash } = url.parse(redirectPath, true);

  const queryObj = allowRedirectBack
    ? {
        [REDIRECT_QUERY_PARAM_NAME]: `${location.pathname}${location.search}${location.hash}`,
        ...query,
      }
    : {
        ...query,
      };

  const queryAr = [];

  for (const property in queryObj) {
    queryAr.push(`${property}=${encodeURIComponent(queryObj[property])}`);
  }

  return {
    pathname,
    hash,
    search: queryAr.join('&'),
  };
};

const reduxAuthWrapper = ({ redirectPath, allowRedirectBack, authenticatedSelector }) => {
  const wrapComponent = (DecoratedComponent) => (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isAuthenticated = useSelector(authenticatedSelector);

    useLayoutEffect(() => {
      if (!isAuthenticated) {
        setTimeout(() => navigate(createRedirectLoc(allowRedirectBack, location, redirectPath), { replace: true }), 100);
      }
    }, [isAuthenticated]);

    if (isAuthenticated) {
      return <DecoratedComponent {...props} />;
    }

    return null;
  };

  return (DecoratedComponent) => wrapComponent(DecoratedComponent);
};

export const UserIsAuthenticated = reduxAuthWrapper({
  redirectPath: '/login',
  allowRedirectBack: true,
  authenticatedSelector: selectIsAuthenticated,
});

export const ReportsShown = reduxAuthWrapper({
  redirectPath: '/login',
  allowRedirectBack: false,
  authenticatedSelector: (state) =>
    getShowReportsFlag(state.appConfig.config) && hasAccess(reportsAccessId, get(state.login.user, 'currentPermissions')),
});

export const CustomersShown = reduxAuthWrapper({
  redirectPath: '/login',
  allowRedirectBack: false,
  authenticatedSelector: (state) => getShowDictionaryForSAFlag(state.appConfig.config),
});

export const MultiFwUpdateShown = reduxAuthWrapper({
  redirectPath: '/chargers/chargers_list',
  allowRedirectBack: false,
  authenticatedSelector: (state) =>
    getFirmwareUpdateFlag(state.appConfig.config) &&
    hasAccess(viewFirmwareUpdatePageAccessId, get(state.login.user, 'currentPermissions'), state.appConfig.config),
});

export const UserIsNotAuthenticated = reduxAuthWrapper({
  authenticatedSelector: (state) => !state.login.isAuthenticated,
  redirectPath: '/',
  allowRedirectBack: false,
});

export const ConfigUpdateShow = reduxAuthWrapper({
  redirectPath: '/chargers/chargers_list',
  allowRedirectBack: false,
  authenticatedSelector: (state) => {
    const config = get(state, 'appConfig.config');
    const permissions = get(state, 'login.user.currentPermissions');
    const show = getShowConfigUpdate(config);
    const hasPermission = hasAccess(viewChargerConfigsId, permissions, config);

    return show && hasPermission;
  },
  wrapperDisplayName: 'ConfigUpdateShow',
});
